import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { usePreviousDistinct } from 'react-use';

import useAssets from '~hooks/useAssets';
import useDelayFn from '~hooks/useDelayFn';
import { queries } from '~modules/media-library/controller/api/Queries';
import { AssetId } from '~modules/media-library/model/Asset';
import { ShareAction } from '~modules/media-library/view/ShareModal/ShareAction';
import { ShareModal } from '~modules/media-library/view/ShareModal/ShareModal';

export function ShareModalController({
  assetId,
  onClose,
  shareAction = ShareAction.Download,
}: {
  assetId: AssetId;
  onClose: () => void;
  shareAction?: ShareAction;
}) {
  const { getAssetById } = useAssets();
  const [isOpen, setIsOpen] = useState(false);
  const delayedOnClose = useDelayFn(onClose, 500);

  // get asset from store
  const asset = getAssetById(assetId);
  const prevAsset = usePreviousDistinct(asset, (_, next) => !Boolean(next));

  // derived
  const hasRestrictions = Boolean(asset?.attributes.legalNotice || asset?.attributes.usageNotice);

  // load download_formats and update asset in asset store
  const { data: downloadFormats } = useQuery({
    ...queries.mediaLibrary.downloadFormats(assetId),
    enabled: Boolean(assetId),
    staleTime: 1 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // load download_formats and update asset in asset store
  const { data: downloadVariations } = useQuery({
    ...queries.mediaLibrary.downloadVariations(assetId),
    enabled: Boolean(assetId),
    staleTime: 1 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // open details modal when a asset is set
  useEffect(() => {
    setIsOpen(Boolean(asset));
  }, [asset]);

  // Handler: close dialog, but delay the closing to have smoother transitions
  const handleOnClose = useCallback(() => {
    setIsOpen(false);
    delayedOnClose();
  }, [delayedOnClose]);

  return (
    <ShareModal
      isOpen={isOpen}
      assetId={asset?.id || prevAsset?.id}
      downloadFormats={downloadFormats}
      downloadVariations={downloadVariations}
      onClose={handleOnClose}
      hasRestrictions={hasRestrictions}
      shareAction={shareAction}
    />
  );
}
